import type { Layer } from '@geosoft/viewer-sdk';
import { normalizeTitilerSourceHost, toBrowserTitilerUrl, ensureTitilerPngTileTemplate, withTitilerTileJsonPngFormat } from '@/utils/titilerSourceHost';
import { tilePixelRatioFor, withDisplayTileSize } from '@/utils/rasterTileResolution';
import { attachHillshadeTileResolution } from '@/utils/hillshade';
import { isLikelySingleBandRaster as isLikelySingleBandHint } from '@/utils/titilerRasterBands';
import {
  colorizeDemTileTemplate,
  fetchTitilerRescaleRange,
  terrainDerivativeColorRole,
  terrainDerivativeRenderParams,
} from '@/utils/titilerDemColorize';
import { requestedBandCount } from '@/utils/titilerRasterProfile';
import { activeRenderQualityProfile } from './renderQualityProfile';
import { rampToTitilerColormap as rasterRampToTitilerColormap } from '@/components/raster/rasterClassify';
import {
  elevationMeshSourceUrl,
  shouldUseElevationMesh,
} from './elevationMeshPolicy';

export interface Giro3DInstanceOptions {
  target: HTMLDivElement;
  crs: unknown;
  /** Renderer parameters — `logarithmicDepthBuffer: true` is critical for wide near/far ranges. */
  renderer?: {
    antialias?: boolean;
    logarithmicDepthBuffer?: boolean;
    alpha?: boolean;
    powerPreference?: 'default' | 'high-performance' | 'low-power';
  };
}

export interface Giro3DModules {
  readonly Instance: new (options: Giro3DInstanceOptions) => Giro3DInstance;
  readonly CoordinateSystem: { readonly epsg3857: unknown };
  readonly Extent: new (
    crs: unknown,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
  ) => unknown;
  readonly Map: new (options: {
    name: string;
    extent: unknown;
    terrain?: boolean;
    backgroundOpacity?: number;
    discardNoData?: boolean;
    subdivisionStrategy?: unknown;
    lighting?: {
      enabled?: boolean;
      mode?: number;
      elevationLayersOnly?: boolean;
      hillshadeIntensity?: number;
      hillshadeAzimuth?: number;
      hillshadeZenith?: number;
      zFactor?: number;
    };
  }) => Giro3DMap;
  readonly allLayersLoadedSubdivisionStrategy?: unknown;
  readonly XYZ: new (
    options: { url: string; crossOrigin?: string; minZoom?: number; maxZoom?: number; tilePixelRatio?: number },
  ) => unknown;
  readonly TiledImageSource: new (
    options: {
      source: unknown;
      enableWorkers?: boolean;
      fadeDuration?: number;
      extent?: unknown;
      format?: unknown;
      httpTimeout?: number;
      /**
       * No-data sentinel. Giro3D masks pixels equal to this value (returned as
       * transparent / no-geometry). Used on the elevation `.tif` path so DEM
       * nodata (e.g. -32767) does not become a false terrain spike. */
      noDataValue?: number;
    },
  ) => unknown;
  readonly ColorLayer: new (
    options: { name: string; source: unknown; extent?: unknown; preloadImages?: boolean; resolutionFactor?: number },
  ) => unknown;
  readonly ElevationLayer: new (options: {
    name: string;
    source: unknown;
    extent?: unknown;
    minmax?: { min: number; max: number };
    /**
     * Giro3D nodata handling. `ElevationLayer` defaults to
     * `{ replaceNoData: false }`, which leaves holes and steep walls wherever a
     * DEM is masked — a rotated/sparse COG then renders as broken fragments. */
    noDataOptions?: {
      replaceNoData: boolean;
      maxSearchDistance?: number;
      alpha?: number;
    };
  }) => unknown;
  readonly GeoTIFFFormat: new (options?: { enableWorkers?: boolean }) => unknown;
  readonly Tiles3D: new (options: { id: string; url?: string }) => unknown;
  readonly COPCSource: new (options: { url: string; decimate?: number; enableWorkers?: boolean }) => unknown;
  readonly PotreeSource: new (options: { url: string; enableWorkers?: boolean }) => unknown;
  readonly PointCloud: new (options: { name?: string; source: unknown }) => unknown;
  readonly PotreeCore: new () => {
    pointBudget: number;
    loadPointCloud(url: string, baseUrlOrRequestManager: unknown): Promise<unknown>;
    updatePointClouds(pointClouds: unknown[], camera: unknown, renderer: unknown): unknown;
  };
  readonly GLTFLoader: new () => { loadAsync(url: string): Promise<{ scene: unknown }> };
}

export interface Giro3DInstance {
  add(object: unknown): Promise<unknown>;
  remove?(object: unknown): void;
  dispose?(): void;
}

export interface Giro3DMap {
  addLayer(layer: unknown): Promise<unknown>;
  removeLayer?(layer: unknown, options?: { disposeLayer?: boolean }): boolean;
  traverseTiles?(cb: (tile: unknown) => void): void;
}

export type Giro3DLayerEntityKind =
  | 'raster-source'
  | 'native-vector-pmtiles'
  | 'terrain-source'
  | 'terrain-color-source'
  | '3dtiles'
  | 'bim-gltf'
  | 'copc-pointcloud'
  | 'pointcloud-tiles3d'
  | 'potree-pointcloud';

export interface Giro3DLayerEntityDescriptor {
  readonly id: string;
  readonly kind: Giro3DLayerEntityKind;
  readonly url: string;
  readonly attachTo: 'map-layer' | 'instance-object';
  readonly entity?: unknown;
  /** Pass-through of `layer.style` so the entity factory can read hints like
   * `maxZoom` for non-TiTiler raster sources (e.g. OSM basemap). */
  readonly style?: Record<string, unknown>;
  /** M8 — overlay rendering mode for 3D Tiles in overlays slot */
  readonly isOverlay?: boolean;
  /** Screen-space error threshold for 3D Tiles (pixels). Lower = more tiles. */
  readonly errorTarget?: number;
  /**
   * G20 — explicit `[min, max]` rescale for the `terrain-color-source` colormap,
   * in DEM data units. When set, the colour ramp spans this exact range instead
   * of TiTiler's 2–98th percentile. Used by the hypsometric drape paired with an
   * `ElevationLayer` (flag ON) so the colours span the real relief and align with
   * the mesh's true min/max.
   */
  readonly colorRescaleRange?: readonly [number, number];
  /**
   * G20 — TiTiler `colormap_name` for the `terrain-color-source` drape. Lets the
   * terrain colour-ramp selector swap the hypsometric drape's colormap (e.g.
   * `terrain`, `plasma`, `rdylgn`, `gray`). Defaults to `terrain` when omitted so
   * the existing flat-path / default drape behaviour is unchanged.
   */
  readonly colormapName?: string;
  /**
   * Explicit TiTiler JSON colormap. Used for GeoViewer ramps that TiTiler does
   * not ship as a named preset, notably `arcgis-elevation`.
   */
  readonly customColormap?: string;
}

/**
 * Per-layer SSE (Screen Space Error) threshold tuned for PLATEAU multi-layer
 * Tokyo scenes.
 *
 * IMPORTANT — Single-LOD vs multi-LOD policy:
 *
 *   PLATEAU ships `tran` (roads), `frn` (furniture) and `veg` (vegetation)
 *   only as **single-LOD-3 tilesets**: leaves have no children to subdivide
 *   into. Setting `errorTarget` aggressively low (e.g. 2.5) for these layers
 *   makes Giro3D's tile manager keep flagging visible tiles as "needs
 *   subdivision" on close approach. With no children to load, it hides the
 *   leaf while it waits → the user sees the layer disappear when they zoom in.
 *
 *   We therefore use a higher `errorTarget` (≥ 8) for single-LOD leaf-only
 *   layers so the leaf stays rendered at close zoom, and reserve aggressive
 *   targets for multi-LOD chains (buildings LOD1→LOD2, bridges LOD1→LOD2).
 */
export function resolveTileErrorTarget(layerId: string, layerLabel?: string): number {
  const text = `${layerId} ${layerLabel ?? ''}`.toLowerCase();
  if (
    text.includes('tran')
    || text.includes('road')
    || text.includes('frn')
    || text.includes('furniture')
    || text.includes('vegetation')
    || text.includes('tree')
    || text.includes('veg')
  ) {
    return 8;
  }
  if (text.includes('lod3')) {
    return 4;
  }
  if (text.includes('brid') || text.includes('bridge')) {
    return 2;
  }
  if (text.includes('bldg') || text.includes('building')) {
    if (/lod1|no.?texture|no_texture/.test(text)) return 5;
    return 3;
  }
  return 4;
}

export async function loadGiro3DModules(): Promise<Giro3DModules> {
  const [
    { default: Instance },
    { default: CoordinateSystem },
    { default: Extent },
    { default: MapEntity, allLayersLoadedSubdivisionStrategy },
    { default: XYZ },
    { default: TiledImageSource },
    { ColorLayer },
    { default: ElevationLayer },
    { default: Tiles3D },
    { default: COPCSource },
    { default: PotreeSource },
    { default: PointCloud },
    { Potree },
    { GLTFLoader },
    { default: GeoTIFFFormat },
  ] = await Promise.all([
      import('@giro3d/giro3d/core/Instance.js'),
      import('@giro3d/giro3d/core/geographic/CoordinateSystem.js'),
      import('@giro3d/giro3d/core/geographic/Extent.js'),
      import('@giro3d/giro3d/entities/Map.js'),
      import('ol/source/XYZ.js'),
      import('@giro3d/giro3d/sources/TiledImageSource.js'),
      import('@giro3d/giro3d/core/layer/ColorLayer.js'),
      import('@giro3d/giro3d/core/layer/ElevationLayer.js'),
      import('@giro3d/giro3d/entities/Tiles3D.js'),
      import('@giro3d/giro3d/sources/COPCSource.js'),
      import('@giro3d/giro3d/sources/PotreeSource.js'),
      import('@giro3d/giro3d/entities/PointCloud.js'),
      import('potree-core'),
      import('three/examples/jsm/loaders/GLTFLoader.js'),
      import('@giro3d/giro3d/formats/GeoTIFFFormat.js'),
    ]);

  return {
    Instance: Instance as Giro3DModules['Instance'],
    CoordinateSystem,
    Extent: Extent as Giro3DModules['Extent'],
    Map: MapEntity as Giro3DModules['Map'],
    allLayersLoadedSubdivisionStrategy,
    XYZ: XYZ as Giro3DModules['XYZ'],
    TiledImageSource: TiledImageSource as Giro3DModules['TiledImageSource'],
    ColorLayer: ColorLayer as Giro3DModules['ColorLayer'],
    ElevationLayer: ElevationLayer as Giro3DModules['ElevationLayer'],
    Tiles3D: Tiles3D as Giro3DModules['Tiles3D'],
    COPCSource: COPCSource as Giro3DModules['COPCSource'],
    PotreeSource: PotreeSource as Giro3DModules['PotreeSource'],
    PointCloud: PointCloud as Giro3DModules['PointCloud'],
    PotreeCore: Potree as Giro3DModules['PotreeCore'],
    GLTFLoader: GLTFLoader as Giro3DModules['GLTFLoader'],
    GeoTIFFFormat: GeoTIFFFormat as Giro3DModules['GeoTIFFFormat'],
  };
}

export function describeGiro3DLayer(layer: Layer, slot?: string): Giro3DLayerEntityDescriptor {
  if (layer.kind === 'vector') {
    return {
      id: layer.id,
      kind: 'native-vector-pmtiles',
      url: requireEndpoint(layer, layer.endpoints.pmtiles, 'pmtiles'),
      attachTo: 'instance-object',
      style: layer.style as Record<string, unknown> | undefined,
    };
  }
  if (layer.kind === 'raster') {
    // ArcGIS Pro–style true 3D: DEM/DTM COGs become an ElevationLayer mesh with
    // a ColorLayer drape, not a flat greyscale ColorLayer. Imagery and DEM
    // derivatives (hillshade/slope/aspect) stay on the ColorLayer path.
    if (shouldUseElevationMesh(layer)) {
      return {
        id: layer.id,
        kind: 'terrain-source',
        url: requireEndpoint(layer, elevationMeshSourceUrl(layer), 'tiles'),
        attachTo: 'map-layer',
        style: layer.style as Record<string, unknown> | undefined,
      };
    }
    return {
      id: layer.id,
      kind: 'raster-source',
      url: requireEndpoint(layer, layer.endpoints.tiles, 'tiles'),
      attachTo: 'map-layer',
      style: layer.style as Record<string, unknown> | undefined,
    };
  }
  if (layer.kind === 'terrain') {
    // Spike (gap G20): when the elevation-mesh feature flag is ON, route terrain
    // to the `terrain-source` → Giro3D `ElevationLayer` path (real DEM heights →
    // 3D relief mesh). Default OFF keeps the stable flat `terrain-color-source`
    // ColorLayer (TiTiler `colormap_name=terrain`, applied server-side), which
    // is the Phase 1b display path. DEM-like *rasters* use the mesh without
    // this flag (see shouldUseElevationMesh).
    if (shouldUseElevationMesh(layer)) {
      return {
        id: layer.id,
        kind: 'terrain-source',
        url: requireEndpoint(layer, layer.endpoints.terrain, 'terrain'),
        attachTo: 'map-layer',
        style: layer.style as Record<string, unknown> | undefined,
      };
    }
    return {
      id: layer.id,
      kind: 'terrain-color-source',
      url: requireEndpoint(layer, layer.endpoints.terrain, 'terrain'),
      attachTo: 'map-layer',
      style: layer.style as Record<string, unknown> | undefined,
    };
  }
  if (layer.kind === '3dtiles') {
    // Mark layers as "overlay" for the material-override pipeline when EITHER
    // (a) the scene preset places them in the `overlays` slot, OR
    // (b) the layer id/label matches a known overlay feature class (underground
    //     buildings, flood, high-tide, landslide) — these ship in `realityData`
    //     in some manifests but still need the translucent overlay treatment so
    //     users can visually distinguish them from above-ground reality data.
    const text = `${layer.id} ${layer.label ?? ''}`.toLowerCase();
    const isOverlayByFeatureClass =
      text.includes('underground')
      || /\bubld\b/.test(text)
      || text.includes('flood')
      || /\bfld\b/.test(text)
      || text.includes('high-tide')
      || /\bhtd\b/.test(text)
      || text.includes('landslide')
      || /\blsld\b/.test(text);
    const isOverlay = slot === 'overlays' || isOverlayByFeatureClass;
    return {
      id: layer.id,
      kind: '3dtiles',
      url: requireEndpoint(layer, layer.endpoints.tileset3d, 'tileset3d'),
      attachTo: 'instance-object',
      isOverlay,
      errorTarget: resolveTileErrorTarget(layer.id, layer.label),
    };
  }
  if (layer.kind === 'bim') {
    return {
      id: layer.id,
      kind: 'bim-gltf',
      url: requireEndpoint(layer, layer.endpoints.model ?? layer.endpoints.tileset3d, 'model or tileset3d'),
      attachTo: 'instance-object',
      style: layer.style as Record<string, unknown> | undefined,
    };
  }
  if (layer.kind === 'pointcloud') {
    if (layer.endpoints.tileset3d) {
      return {
        id: layer.id,
        kind: 'pointcloud-tiles3d',
        url: layer.endpoints.tileset3d,
        attachTo: 'instance-object',
      };
    }
    const copcUrl = layer.endpoints.copc ?? (
      isCopcUrl(layer.endpoints.pointcloud) ? layer.endpoints.pointcloud : undefined
    );
    if (copcUrl) {
      return {
        id: layer.id,
        kind: 'copc-pointcloud',
        url: requireEndpoint(layer, copcUrl, 'copc'),
        attachTo: 'instance-object',
      };
    }
    const potreeUrl = layer.endpoints.potree ?? (
      isPotreeMetadataUrl(layer.endpoints.pointcloud) ? layer.endpoints.pointcloud : undefined
    );
    if (potreeUrl) {
      return {
        id: layer.id,
        kind: 'potree-pointcloud',
        url: requireEndpoint(layer, potreeUrl, 'potree'),
        attachTo: 'instance-object',
      };
    }
    const url = layer.endpoints.pointcloud || '';
    return {
      id: layer.id,
      kind: 'copc-pointcloud',
      url: requireEndpoint(layer, isCopcUrl(url) ? url : undefined, 'copc'),
      attachTo: 'instance-object',
    };
  }
  throw new Error(`Giro3D does not support layer kind '${layer.kind}'`);
}

/**
 * G20 — build the descriptor for a hypsometric ColorLayer drape paired with a
 * terrain `ElevationLayer` (flag ON only).
 *
 * The drape reuses the `terrain-color-source` builder (TiTiler `colormap_name=
 * terrain`) but is rescaled to the ElevationLayer's TRUE data `[min, max]` so the
 * colour ramp spans the real relief and aligns with the mesh. When a usable
 * range is not available the `colorRescaleRange` is omitted and the builder
 * falls back to TiTiler's percentile stretch.
 *
 * Kept pure (no Giro3D / fetch) so the pairing decision is unit-testable.
 */
/**
 * Drape descriptor for a DEM **raster** that now renders as an ElevationLayer
 * mesh (Turkey DTM and friends).
 *
 * These layers already have symbology the operator controls from the Raster
 * card — stretch, greyscale / colour scheme, shaded relief. Draping the fixed
 * hypsometric `terrain` ramp over the mesh would silently override that choice,
 * so the drape reuses the layer's own visual tiles URL and therefore looks
 * exactly like the 2D map.
 */
export function buildDemRasterDrapeDescriptor(
  terrain: Giro3DLayerEntityDescriptor,
  tilesUrl: string,
): Giro3DLayerEntityDescriptor {
  return {
    // Keep the `::hypso` suffix: the adapter's drape bookkeeping and the layer
    // diagnostics id list already key off it.
    id: `${terrain.id}::hypso`,
    kind: 'raster-source',
    url: tilesUrl,
    attachTo: 'map-layer',
    ...(terrain.style ? { style: terrain.style } : {}),
  };
}

export function buildTerrainColorDrapeDescriptor(
  terrain: Giro3DLayerEntityDescriptor,
  minmax?: { min?: number; max?: number } | null,
  colormapName?: string,
): Giro3DLayerEntityDescriptor {
  const hasRange = !!minmax
    && typeof minmax.min === 'number' && Number.isFinite(minmax.min)
    && typeof minmax.max === 'number' && Number.isFinite(minmax.max)
    && (minmax.max as number) > (minmax.min as number);
  return {
    id: `${terrain.id}::hypso`,
    kind: 'terrain-color-source',
    url: terrain.url,
    attachTo: 'map-layer',
    ...(terrain.style ? { style: terrain.style } : {}),
    ...(hasRange
      ? { colorRescaleRange: [minmax!.min as number, minmax!.max as number] as const }
      : {}),
    // Default `terrain` is applied by the builder when omitted, keeping the
    // initial drape identical to before; the ramp selector passes an override.
    ...(colormapName === 'arcgis-elevation'
      ? { customColormap: rasterRampToTitilerColormap('arcgis-elevation') }
      : typeof colormapName === 'string' && colormapName.length > 0 && colormapName !== 'terrain'
        ? { colormapName }
        : {}),
  };
}

/** Create the single terrain-capable world map used by the 3D view. */
/**
 * Build the Giro3D `Map` (the tiled ground surface every ColorLayer/ElevationLayer
 * attaches to).
 *
 * `extent` defaults to the whole Web Mercator world, which is what a basemap or a
 * global raster stack needs. Pass a DEM's own extent instead when the scene is a
 * local elevation mesh: a world-extent quadtree needs ~13 subdivision levels to
 * reach a 10 km DEM, and at every level 3 of 4 children are empty, which starves
 * the tile pipeline on a software-WebGL host and leaves a flat z=0 plane running
 * to the horizon around the relief.
 *
 * `discardNoData` may only be enabled on a Map that is guaranteed to carry an
 * ElevationLayer. Giro3D's `TileFS` discards *every* fragment when the define is
 * set and no elevation layer is present, so switching it on for a raster-only Map
 * renders an empty scene.
 */
export function createGiro3DMap(
  modules: Giro3DModules,
  options: { extent?: unknown; discardNoData?: boolean } = {},
): Giro3DMap {
  const worldExtent = new modules.Extent(
    modules.CoordinateSystem.epsg3857,
    -20037508.34,
    20037508.34,
    -20037508.34,
    20037508.34,
  );
  return new modules.Map({
    name: 'geoviewer-giro3d-map',
    extent: options.extent ?? worldExtent,
    terrain: true,
    // Giro3D's Map defaults to opaque `#0a3b59`. With stacked ColorLayers
    // (COG / hillshade / slope / aspect) that footprint reads as a leftover
    // "background slab" when layers are hidden. Keep the Map transparent so
    // only real ColorLayer pixels (and the MapLibre underlay) contribute.
    backgroundOpacity: 0,
    // On a slow/software WebGL host, the default strategy can hide a parent as
    // soon as subdivision starts while some child textures are still pending.
    // The result is the giant checkerboard gaps seen in the screenshots.
    // Retain the complete parent until every visible layer has loaded the child.
    subdivisionStrategy: modules.allLayersLoadedSubdivisionStrategy,
    // Compiles in the shader's `isNoData` test. Without it a masked DEM sample
    // renders at height 0, so every valid patch of a sparse COG becomes an island
    // with a cliff down to the ellipsoid. Opt-in per the caveat above: the same
    // define blanks a Map that has no ElevationLayer.
    ...(options.discardNoData ? { discardNoData: true } : {}),
    // Giro3D v2 uses `enabled` separately from `mode`; mode 0 is Hillshade.
    lighting: {
      enabled: true,
      mode: 0,
      elevationLayersOnly: false,
      hillshadeAzimuth: 315,
      hillshadeZenith: 45,
      hillshadeIntensity: 0.72,
      zFactor: 1,
    },
  });
}

/**
 * Build a default world basemap as a Giro3D `ColorLayer`.
 *
 * Uses OpenStreetMap with a hard `maxZoom: 18` cap. Rationale:
 *  - OSM tiles work reliably and load fast from most locations (no API key,
 *    well-distributed CDN, permissive CORS up to z=18).
 *  - At z=19+ OSM intermittently returns 404 / CORS-blocked, which the
 *    Giro3D LOD selector then retries forever, starving the request queue
 *    and blocking everything else (including local ortho tiles).
 *  - Capping the source at z=18 keeps it inside the safe range. Giro3D
 *    overzooms (upsamples) the z=18 tiles when the camera goes closer.
 *  - Higher-resolution detail at the actual site (Dubai) is supplied by
 *    the user's own 15cm/30cm ortho TIFFs (served locally via TiTiler),
 *    which keep loading at native pixel resolution thanks to their own
 *    tilejson-derived extent + zoom range — the basemap is just background.
 *
 * Switched away from Esri World Imagery because the user reported repeated
 * `timeout` errors against `services.arcgisonline.com` from their network.
 */
export function createDefaultOsmBasemapLayer(modules: Giro3DModules): unknown {
  const source = new modules.TiledImageSource({
    source: new modules.XYZ({
      url: defaultBasemapTileUrl(),
      crossOrigin: 'anonymous',
      minZoom: 0,
      maxZoom: 18,
    }),
    enableWorkers: true,
    // Reduce fade to 0 so there's no blank gap during LOD transitions when
    // zooming out — coarse tiles appear immediately instead of fading in.
    fadeDuration: 0,
  });
  return new modules.ColorLayer({
    name: 'basemap-osm',
    source,
    preloadImages: true,
    resolutionFactor: activeRenderQualityProfile().rasterResolutionFactor,
  });
}

/**
 * Tile URL for the 3D scene's basemap.
 *
 * Corporate proxies routinely block `tile.openstreetmap.org` and the Esri CDN
 * outright (`ERR_TUNNEL_CONNECTION_FAILED`), which is why the 2D default basemap
 * goes through Vite's same-origin `/basemap-carto` proxy. The 3D scene has to
 * take the same route or it silently ends up with no basemap at all.
 */
function defaultBasemapTileUrl(): string {
  const path = '/basemap-carto/light_all/{z}/{x}/{y}.png';
  return typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
}

export async function createGiro3DEntity(
  descriptor: Giro3DLayerEntityDescriptor,
  modules: Pick<
    Giro3DModules,
    'XYZ' | 'TiledImageSource' | 'ColorLayer' | 'ElevationLayer' | 'GeoTIFFFormat' | 'Tiles3D' | 'COPCSource' | 'PotreeSource' | 'PointCloud' | 'PotreeCore' | 'GLTFLoader' | 'Extent' | 'CoordinateSystem'
  >,
): Promise<unknown> {
  if (descriptor.kind === 'raster-source') {
    const tilesUrl = normalizeTitilerSourceHost(descriptor.url);
    const tj = await fetchTitilerTilejson(tilesUrl);
    // Prefer the layer template (already has .png + DEM stretch from
    // sceneItemToLayer). TileJSON only supplies bounds / zoom — using
    // tj.tiles replaces colormap with JPEG and paints static on first open.
    const normalizedTilesUrl = normalizeRasterBandsForDescriptor(tilesUrl, descriptor);
    const derivativeTilesUrl = await colorizeTerrainDerivativeRaster(normalizedTilesUrl, descriptor);
    // Derivatives already have colormap + nodata from colorizeTerrainDerivativeRaster.
    // Running colorizeDemTileTemplate on them would overwrite viridis/hsv with gray.
    const isTerrainDerivative = terrainDerivativeColorRole({ style: descriptor.style }) !== null;
    const visualTilesUrl = withDisplayTileSize(
      ensureTitilerPngTileTemplate(
        isTerrainDerivative
          ? derivativeTilesUrl
          : await colorizeDemTileTemplate(derivativeTilesUrl, {
              id: descriptor.id,
              kind: 'raster',
              label: typeof descriptor.style?.title === 'string' ? descriptor.style.title : undefined,
              style: descriptor.style,
              endpoints: { tiles: derivativeTilesUrl },
            }),
      ),
    );
    const styleMaxZoom = typeof descriptor.style?.maxZoom === 'number' ? (descriptor.style.maxZoom as number) : undefined;
    const styleMinZoom = typeof descriptor.style?.minZoom === 'number' ? (descriptor.style.minZoom as number) : undefined;
    // Read the ratio off the URL actually being requested — the tilejson echo
    // reflects the pre-colourisation template and misses the HiDPI upgrade.
    const tilePixelRatio = tilePixelRatioFor(visualTilesUrl);
    const zoomOpts = tj
      ? { minZoom: tj.minzoom, maxZoom: tj.maxzoom }
      : {
          ...(styleMinZoom !== undefined ? { minZoom: styleMinZoom } : {}),
          ...(styleMaxZoom !== undefined ? { maxZoom: styleMaxZoom } : {}),
        };
    const extent = tj ? lonLatBoundsToEpsg3857Extent(tj.bounds, modules) : undefined;
    const xyzSource = new modules.XYZ({
      url: visualTilesUrl,
      crossOrigin: 'anonymous',
      ...(tilePixelRatio ? { tilePixelRatio } : {}),
      ...zoomOpts,
    });
    // A hillshade template cannot state its own ground resolution; the tile
    // coordinate carries it (see utils/hillshade.ts).
    attachHillshadeTileResolution(xyzSource, visualTilesUrl);
    const source = new modules.TiledImageSource({
      source: xyzSource,
      // G20 — constrain tile requests to the COG footprint (tilejson bounds).
      // Without an extent on the SOURCE (the ColorLayer extent alone is not
      // enough), Giro3D's XYZ source requests neighbour/edge tiles outside the
      // small DEM-derivative coverage (hillshade/slope/aspect), which TiTiler
      // answers with 404s — the console "404 spam". Matching the terrain-color
      // drape builder, the extent on the source quiets those out-of-footprint
      // requests while the data tiles still render.
      ...(extent ? { extent } : {}),
      enableWorkers: true,
      // Match terrain-color: fade leaves interim opaque tiles that look like
      // a residual footprint when stacked rasters are eye-toggled off.
      fadeDuration: 0,
      // COG-backed rasters (TiTiler) can be slow on cold reads; the OSM XYZ
      // basemap is unaffected. Raise above the 5s default to avoid aborted
      // tile fetches that leave the raster unpainted.
      httpTimeout: 30000,
    });
    console.log(
      `[giro3dRuntime] raster ColorLayer created: id=${descriptor.id}, tilejson=${tj ? 'yes' : 'no'}, extent=${extent ? 'yes' : 'world'}, maxZoom=${tj?.maxzoom ?? styleMaxZoom ?? 'default'}`,
    );
    return new modules.ColorLayer({
      name: descriptor.id,
      source,
      // preloadImages paints a solid extent "fallback" into the composer
      // (often white/black for TiTiler nodata). That fill survives eye-hide
      // as residual boxes — load tiles on demand instead.
      preloadImages: false,
      // Below 1 on a GPU-less host: coarser textures, fewer tile requests, and
      // far less per-frame sampling work (see renderQualityProfile).
      resolutionFactor: activeRenderQualityProfile().rasterResolutionFactor,
      ...(extent ? { extent } : {}),
    });
  }
  if (descriptor.kind === 'terrain-color-source') {
    const tilesUrl = normalizeTitilerSourceHost(descriptor.url);
    console.log('[giro3dRuntime][terrain-color] descriptor.url=', descriptor.url);
    console.log('[giro3dRuntime][terrain-color] normalized tilesUrl=', tilesUrl);
    const tj = await fetchTitilerTilejson(tilesUrl);
    console.log('[giro3dRuntime][terrain-color] tilejson=', tj
      ? { tiles: tj.tiles, bounds: tj.bounds, minzoom: tj.minzoom, maxzoom: tj.maxzoom }
      : 'NULL');
    // G20: when paired with an ElevationLayer (flag ON), the caller passes the
    // DEM's TRUE min/max so the hypsometric ramp spans the real relief and lines
    // up with the mesh; otherwise fall back to TiTiler's 2–98th percentile.
    const rescale = descriptor.colorRescaleRange
      ?? await fetchTitilerCogRescale(tilesUrl);
    // Colorize the single-band DEM so it is always visible draped on the map.
    // The colormap is `terrain` by default but the terrain colour-ramp selector
    // can swap it (plasma / rdylgn / gray) by rebuilding the drape descriptor.
    const colormapName = typeof descriptor.colormapName === 'string' && descriptor.colormapName.length > 0
      ? descriptor.colormapName
      : 'terrain';
    const colorParams: Record<string, string> = descriptor.customColormap
      ? { colormap: descriptor.customColormap }
      : { colormap_name: colormapName };
    // No-data masking for the COLOUR drape (gap G20): without it, the `terrain`
    // colormap paints nodata pixels (e.g. the float-max sentinel `1.70141e+38`)
    // with the rescaled-min colour — water-blue — producing a misleading flat
    // blue slab around the relief. Resolve the sentinel exactly like the
    // elevation path (dataset style hint → TiTiler `/cog/info`) and pass it so
    // nodata pixels come back TRANSPARENT and the basemap shows through.
    // NOTE: genuine 0 m elevation (real sea level / flats) is valid data and is
    // intentionally NOT masked — it stays drawn (blue) and coplanar at z=0.
    const nodataValue = readNumericStyleValue(descriptor.style, 'nodataValue')
      ?? readNumericStyleValue(descriptor.style, 'titilerNodata')
      ?? (await fetchTitilerCogNodata(tilesUrl))
      ?? undefined;
    if (nodataValue !== undefined) {
      colorParams.nodata = String(nodataValue);
    }
    console.log('[giro3dRuntime][terrain-color] nodata=', nodataValue ?? '(none)');
    if (rescale) colorParams.rescale = `${rescale[0]},${rescale[1]}`;
    const coloredTemplate = withDisplayTileSize(
      appendTileTemplateParams(tilesUrl, colorParams),
    );
    console.log('[giro3dRuntime][terrain-color] final XYZ url=', coloredTemplate);
    const extent = tj ? lonLatBoundsToEpsg3857Extent(tj.bounds, modules) : undefined;
    const source = new modules.TiledImageSource({
      source: new modules.XYZ({
        url: coloredTemplate,
        crossOrigin: 'anonymous',
        tilePixelRatio: tilePixelRatioFor(coloredTemplate),
        ...(tj ? { minZoom: tj.minzoom, maxZoom: tj.maxzoom } : {}),
      }),
      // Constrain tile requests to the DEM footprint — without this the OL XYZ
      // source requests boundary tiles outside the COG coverage, gets 404s from
      // TiTiler, and the LOD transition leaves visible holes.
      ...(extent ? { extent } : {}),
      enableWorkers: true,
      fadeDuration: 0,
      // TiTiler COG reads (colormap + rescale, cold cache) routinely exceed
      // Giro3D's 5s default, which aborts the tile fetch and leaves the DEM
      // unpainted. Give the server room to respond on cold reads.
      httpTimeout: 30000,
    });
    console.log('[giro3dRuntime][terrain-color] extent=', extent ? 'set from tilejson bounds' : 'undefined (world)');
    try {
      const layer = new modules.ColorLayer({
        name: descriptor.id,
        source,
        preloadImages: true,
        resolutionFactor: 1,
        ...(extent ? { extent } : {}),
      });
      console.log('[giro3dRuntime][terrain-color] ColorLayer created OK for', descriptor.id);
      return layer;
    } catch (err) {
      console.error('[giro3dRuntime][terrain-color] ColorLayer creation FAILED:', err);
      throw err;
    }
  }
  if (descriptor.kind === 'terrain-source') {
    const tilesUrl = normalizeTitilerSourceHost(descriptor.url);
    console.log('[giro3dRuntime][terrain] descriptor.url=', descriptor.url);
    const tj = await fetchTitilerTilejson(tilesUrl);
    if (!tj) {
      throw new Error(`[giro3dRuntime][terrain] tilejson unavailable for ${descriptor.id}`);
    }
    const [path, query = ''] = (tj.tiles ?? tilesUrl).split('?');
    const search = new URLSearchParams(query);
    search.delete('colormap_name');
    search.delete('rescale');
    search.delete('tilesize');
    // TiTiler defaults to `return_mask=true`, which appends a second Float32
    // sample holding ±FLT_MAX as the validity mask. Giro3D's `GeoTIFFFormat`
    // then takes its `SamplesPerPixel === 2` "grayscale with alpha" branch and
    // copies that sample verbatim into the elevation texture's green channel,
    // where the shader expects 0 = no-data / 1 = valid. Ask for the single-band
    // tile so the decoder synthesizes the mask from `noDataValue` instead.
    search.set('return_mask', 'false');
    // No-data masking (gap G20 bug fix): a DEM's nodata sentinel (e.g. -32767)
    // is a real Float32 value in the COG, so without masking it decodes into
    // geometry — an unrealistic vertical spike/needle. Prefer a dataset hint on
    // `style`, otherwise ask TiTiler for the band nodata via `/cog/info`. We
    // both (a) tell TiTiler to mask server-side (`&nodata=`) and (b) tell Giro3D
    // the sentinel (`TiledImageSource.noDataValue`) so masked samples become
    // holes / no-geometry rather than mesh.
    const nodataValue = readNumericStyleValue(descriptor.style, 'nodataValue')
      ?? readNumericStyleValue(descriptor.style, 'titilerNodata')
      ?? (await fetchTitilerCogNodata(tilesUrl))
      ?? undefined;
    if (nodataValue !== undefined) {
      search.set('nodata', String(nodataValue));
    }
    const tifTemplate = `${path.replace(/\{y\}(\.[a-zA-Z0-9]+)?$/, '{y}.tif')}?${search.toString()}`;
    console.log('[giro3dRuntime][terrain] float tile template=', tifTemplate);
    console.log('[giro3dRuntime][terrain] nodata=', nodataValue ?? '(none)');
    // ElevationLayer needs the TRUE data min/max (not the 2–98th percentile
    // used for colour stretches): the min doubles as the camera "ground floor"
    // for fly-to, and percentiles would clip real relief. Falls back to the
    // percentile rescale, then to Giro3D's own downsampled approximation.
    const minmax = await fetchTitilerCogElevationRange(tilesUrl)
      ?? await fetchTitilerCogRescale(tilesUrl);
    console.log('[giro3dRuntime][terrain] elevation minmax=', minmax);
    const extent = lonLatBoundsToEpsg3857Extent(tj.bounds, modules);
    const source = new modules.TiledImageSource({
      source: new modules.XYZ({
        url: tifTemplate,
        crossOrigin: 'anonymous',
        minZoom: tj.minzoom,
        maxZoom: tj.maxzoom,
      }),
      format: new modules.GeoTIFFFormat(),
      // Constrain requests to the DEM footprint so the XYZ source never
      // asks for tiles at x=0 (Atlantic) when looking at Dubai.
      extent,
      enableWorkers: true,
      httpTimeout: 30000,
      // Mask the DEM nodata sentinel so it never becomes terrain geometry.
      ...(nodataValue !== undefined ? { noDataValue: nodataValue } : {}),
    });
    try {
      const layer = new modules.ElevationLayer({
        name: descriptor.id,
        source,
        extent,
        ...(minmax ? { minmax: { min: minmax[0], max: minmax[1] } } : {}),
        // Giro3D's documented "geometry smoothing" setting for elevation layers:
        // fill masked samples from the nearest valid height so the boundary has
        // no steep wall, while keeping them transparent (`alpha: 0`) so the Map's
        // `discardNoData` still drops them. An earlier attempt at this made the
        // relief worse only because the mask channel was ±FLT_MAX at the time
        // (TiTiler's `return_mask` band); it is a real 0/1 mask now.
        noDataOptions: {
          replaceNoData: true,
          alpha: 0,
          maxSearchDistance: Number.POSITIVE_INFINITY,
        },
      });
      console.log('[giro3dRuntime][terrain] ElevationLayer created OK for', descriptor.id);
      return layer;
    } catch (err) {
      console.error('[giro3dRuntime][terrain] ElevationLayer creation FAILED:', err);
      throw err;
    }
  }
  if (descriptor.kind === 'bim-gltf') {
    const gltf = await new modules.GLTFLoader().loadAsync(descriptor.url);
    return gltf.scene;
  }
  if (descriptor.kind === 'copc-pointcloud') {
    const source = new modules.COPCSource({
      url: descriptor.url,
      // A conservative browser default; UI point budgets can refine this later.
      decimate: 1,
      enableWorkers: true,
    });
    return new modules.PointCloud({ name: descriptor.id, source });
  }
  if (descriptor.kind === 'potree-pointcloud') {
    // Production path for PotreeConverter v2 output:
    // metadata.json + hierarchy.bin + octree.bin.
    //
    // Giro3D's bundled PotreeSource currently targets legacy Potree
    // cloud.js layouts. `potree-core` loads the modern v2 metadata/octree
    // format as a Three.js Object3D, which we can add to the same Giro3D
    // Instance. This keeps Giro3D as the unified render engine while using
    // PotreeConverter purely as the tiler/converter.
    const potree = new modules.PotreeCore();
    potree.pointBudget = 12_000_000;
    const lastSlash = descriptor.url.lastIndexOf('/');
    const filename = lastSlash >= 0 ? descriptor.url.slice(lastSlash + 1) : descriptor.url;
    const baseUrl = lastSlash >= 0 ? descriptor.url.slice(0, lastSlash + 1) : '';
    const requestManager = createPotreeRequestManager(baseUrl);
    const object = await potree.loadPointCloud(filename, requestManager);
    (object as { __geosoftPotreeCore?: unknown }).__geosoftPotreeCore = potree;
    return object;
  }
  if (descriptor.kind === 'pointcloud-tiles3d') {
    return new modules.Tiles3D({
      id: descriptor.id,
      url: descriptor.url,
      // Smoother LOD streaming for point clouds: higher errorTarget loads
      // fewer tiles, reduces the REPLACE-refine swap that causes blinking.
      errorTarget: 16,
    } as { id: string; url?: string; errorTarget?: number });
  }
  return new modules.Tiles3D({
    id: descriptor.id,
    url: descriptor.url,
    errorTarget: descriptor.errorTarget ?? 8,
  } as { id: string; url?: string; errorTarget?: number });
}

function requireEndpoint(layer: Layer, value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(`Layer '${layer.id}' is missing endpoints.${name}`);
  }
  return value;
}

interface TilejsonInfo {
  tiles: string;
  bounds: [number, number, number, number];
  minzoom: number;
  maxzoom: number;
  tilesize?: number;
}

async function fetchTitilerTilejson(tilesUrl: string): Promise<TilejsonInfo | null> {
  const match = tilesUrl.match(
    /^(.*?)\/cog\/tiles\/([^/]+)\/\{z\}\/\{x\}\/\{y\}(?:\.[a-zA-Z]+)?(\?.*)?$/,
  );
  if (!match) return null;
  const [, base, tileMatrixSet, query] = match;
  const tilejsonUrl = withTitilerTileJsonPngFormat(
    `${base}/cog/${tileMatrixSet}/tilejson.json${query ?? ''}`,
  );
  console.log('[giro3dRuntime][tilejson] fetching:', tilejsonUrl);
  try {
    const res = await fetch(tilejsonUrl);
    console.log('[giro3dRuntime][tilejson] response status:', res.status);
    if (!res.ok) return null;
    const tj = (await res.json()) as {
      tiles?: string[];
      bounds?: [number, number, number, number];
      minzoom?: number;
      maxzoom?: number;
      tilesize?: number;
    };
    if (
      !tj.bounds || tj.bounds.length < 4
      || (tj.bounds[0] <= -179.999 && tj.bounds[1] <= -89.999
        && tj.bounds[2] >= 179.999 && tj.bounds[3] >= 89.999)
    ) {
      return null;
    }
    return {
      tiles: ensureTitilerPngTileTemplate(
        toBrowserTitilerUrl(normalizeTitilerSourceHost(
          tj.tiles?.[0]?.startsWith('/cog/')
            ? `${base}${tj.tiles[0]}`
            : tj.tiles?.[0] ?? tilesUrl,
        )),
      ),
      bounds: tj.bounds,
      minzoom: tj.minzoom ?? 0,
      maxzoom: tj.maxzoom ?? 22,
      tilesize: typeof tj.tilesize === 'number' ? tj.tilesize : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Fetch per-band statistics for a COG via TiTiler `/cog/statistics` and derive
 * a robust rescale range (2nd–98th percentile, falling back to min/max) so a
 * colormap stretches across the actual data range instead of clamping to a
 * near-uniform color. Returns `null` on any failure (caller falls back to
 * TiTiler's automatic per-tile stretch).
 */
async function fetchTitilerCogRescale(tilesUrl: string): Promise<[number, number] | null> {
  // Extract `${base}` and the COG `url=` param from the XYZ tile template.
  const baseMatch = tilesUrl.match(/^(.*?)\/cog\/tiles\//);
  const urlParamMatch = tilesUrl.match(/[?&]url=([^&]+)/);
  if (!baseMatch || !urlParamMatch) return null;
  const base = baseMatch[1];
  const cogParam = urlParamMatch[1];
  const statsUrl = `${base}/cog/statistics?url=${cogParam}`;
  console.log('[giro3dRuntime][stats] fetching:', statsUrl);
  try {
    const res = await fetch(statsUrl);
    console.log('[giro3dRuntime][stats] response status:', res.status);
    if (!res.ok) return null;
    const json = (await res.json()) as Record<string, {
      min?: number; max?: number; percentile_2?: number; percentile_98?: number;
    }>;
    const firstBand = Object.values(json)[0];
    if (!firstBand) return null;
    const lo = firstBand.percentile_2 ?? firstBand.min;
    const hi = firstBand.percentile_98 ?? firstBand.max;
    if (typeof lo !== 'number' || typeof hi !== 'number' || !(hi > lo)) return null;
    console.log('[giro3dRuntime][stats] rescale derived=', [lo, hi]);
    return [lo, hi];
  } catch {
    return null;
  }
}

/**
 * Fetch the TRUE elevation min/max for a DEM COG via TiTiler `/cog/statistics`.
 *
 * Distinct from {@link fetchTitilerCogRescale} (2nd–98th percentile, tuned for a
 * pleasing COLOR stretch): an `ElevationLayer` needs the real data range so the
 * terrain mesh bounding volume / height normalisation is correct and the `min`
 * can be reused as the camera ground floor. Returns `null` on any failure so the
 * caller can fall back (percentile, then Giro3D's downsampled approximation).
 */
export async function fetchTitilerCogElevationRange(tilesUrl: string): Promise<[number, number] | null> {
  const baseMatch = tilesUrl.match(/^(.*?)\/cog\/tiles\//);
  const urlParamMatch = tilesUrl.match(/[?&]url=([^&]+)/);
  if (!baseMatch || !urlParamMatch) return null;
  const statsUrl = `${baseMatch[1]}/cog/statistics?url=${urlParamMatch[1]}`;
  try {
    const res = await fetch(statsUrl);
    if (!res.ok) return null;
    const json = (await res.json()) as Record<string, {
      min?: number; max?: number; percentile_2?: number; percentile_98?: number;
    }>;
    const band = Object.values(json)[0];
    if (!band) return null;
    const lo = typeof band.min === 'number' ? band.min : band.percentile_2;
    const hi = typeof band.max === 'number' ? band.max : band.percentile_98;
    if (typeof lo !== 'number' || typeof hi !== 'number' || !(hi > lo)) return null;
    return [lo, hi];
  } catch {
    return null;
  }
}

/**
 * Fetch the DEM's no-data sentinel for a COG via TiTiler `/cog/info`.
 *
 * Used by the elevation `.tif` path when the layer `style` carries no explicit
 * `nodataValue` / `titilerNodata` hint. The nodata value lets us mask the
 * sentinel both server-side (`&nodata=`) and in Giro3D (`noDataValue`) so it
 * never decodes into terrain geometry. Returns `null` on any failure (caller
 * then mounts unmasked, preserving prior behaviour). `/cog/info` reports the
 * value under `nodata_value` (rio-tiler) — we also accept a bare `nodata`.
 */
export async function fetchTitilerCogNodata(tilesUrl: string): Promise<number | null> {
  const baseMatch = tilesUrl.match(/^(.*?)\/cog\/tiles\//);
  const urlParamMatch = tilesUrl.match(/[?&]url=([^&]+)/);
  if (!baseMatch || !urlParamMatch) return null;
  const infoUrl = `${baseMatch[1]}/cog/info?url=${urlParamMatch[1]}`;
  try {
    const res = await fetch(infoUrl);
    if (!res.ok) return null;
    const json = (await res.json()) as { nodata_value?: unknown; nodata?: unknown };
    for (const candidate of [json.nodata_value, json.nodata]) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve a DEM's own EPSG:3857 extent from its TiTiler tilejson bounds.
 *
 * Lets the adapter build the Giro3D `Map` around the elevation data instead of the
 * whole world. Returns `null` when the tilejson is unavailable or carries no usable
 * bounds, in which case the caller keeps the world-extent Map.
 *
 * Note this is the DEM's axis-aligned bounding box: a rotated dataset still has
 * no-data corners inside it, so `discardNoData` / `noDataOptions` remain necessary.
 */
export async function fetchTerrainMapExtent(
  tilesUrl: string,
  modules: Pick<Giro3DModules, 'Extent' | 'CoordinateSystem'>,
): Promise<unknown | null> {
  const tj = await fetchTitilerTilejson(normalizeTitilerSourceHost(tilesUrl));
  if (!tj?.bounds || tj.bounds.length < 4) return null;
  return lonLatBoundsToEpsg3857Extent(tj.bounds, modules);
}

/** Append/override query params on an XYZ tile template URL (handles `{z}/{x}/{y}`). */
function appendTileTemplateParams(template: string, params: Record<string, string>): string {
  const [path, query = ''] = template.split('?');
  const search = new URLSearchParams(query);
  for (const [k, v] of Object.entries(params)) search.set(k, v);
  // URLSearchParams encodes `{`/`}` in the path? No — path is untouched here.
  return `${path}?${search.toString()}`;
}

function readNumericStyleValue(
  style: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const value = style?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

async function colorizeTerrainDerivativeRaster(
  tilesUrl: string,
  descriptor: Giro3DLayerEntityDescriptor,
): Promise<string> {
  const role = terrainDerivativeColorRole({ style: descriptor.style });
  if (!role) return tilesUrl;

  // Role colormap + nodata mask come from the shared TiTiler colorize policy so
  // Giro3D, MapLibre and Canvas 2D paint a companion identically. An unmasked
  // sentinel paints an opaque rectangle over the whole COG footprint.
  const render = terrainDerivativeRenderParams(role);
  const params: Record<string, string> = { colormap_name: render.colormap };

  const nodata = readNumericStyleValue(descriptor.style, 'nodataValue')
    ?? readNumericStyleValue(descriptor.style, 'titilerNodata')
    ?? (await fetchTitilerCogNodata(tilesUrl))
    ?? render.nodataFallback;
  params.nodata = String(nodata);

  const rescale =
    render.rescale
    ?? (await fetchTitilerRescaleRange(tilesUrl, nodata))
    ?? render.fallbackRescale;
  params.rescale = `${rescale[0]},${rescale[1]}`;

  return appendTileTemplateParams(tilesUrl, params);
}

function isLikelySingleBandRasterDescriptor(descriptor: Giro3DLayerEntityDescriptor): boolean {
  if (descriptor.kind === 'terrain-source') return true;
  if (descriptor.kind === 'terrain-color-source') return true;
  return isLikelySingleBandHint({
    id: descriptor.id,
    kind: descriptor.kind,
    title: typeof descriptor.style?.title === 'string' ? descriptor.style.title : null,
    family: typeof descriptor.style?.family === 'string' ? descriptor.style.family : null,
    artifactKind:
      typeof descriptor.style?.gtwinArtifactKind === 'string'
        ? descriptor.style.gtwinArtifactKind
        : typeof descriptor.style?.artifactKind === 'string'
          ? descriptor.style.artifactKind
          : null,
    terrainDerivative:
      typeof descriptor.style?.terrainDerivative === 'string'
        ? descriptor.style.terrainDerivative
        : null,
  });
}

function normalizeRasterBandsForDescriptor(
  tilesUrl: string,
  descriptor: Giro3DLayerEntityDescriptor,
): string {
  // Pre-network default only. An explicit multi-band template (converter RGB
  // output, or a template already corrected from `/cog/info`) is authoritative.
  if (requestedBandCount(tilesUrl) > 1) return tilesUrl;
  if (!isLikelySingleBandRasterDescriptor(descriptor)) return tilesUrl;
  try {
    const url = new URL(tilesUrl);
    url.searchParams.delete('bidx');
    url.searchParams.append('bidx', '1');
    return url.toString().replace(/%7B/g, '{').replace(/%7D/g, '}');
  } catch {
    return tilesUrl.replace(/&bidx=2&bidx=3/g, '');
  }
}

const ORIGIN_SHIFT = 20037508.342789244;

function lonToWebMercatorX(lon: number): number {
  return (lon / 180) * ORIGIN_SHIFT;
}

function latToWebMercatorY(lat: number): number {
  const latRad = (lat * Math.PI) / 180;
  const y = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  return (y / Math.PI) * ORIGIN_SHIFT;
}

function lonLatBoundsToEpsg3857Extent(
  bounds: [number, number, number, number],
  modules: Pick<Giro3DModules, 'Extent' | 'CoordinateSystem'>,
): unknown {
  const [minLon, minLat, maxLon, maxLat] = bounds;
  return new modules.Extent(
    modules.CoordinateSystem.epsg3857,
    lonToWebMercatorX(minLon),
    lonToWebMercatorX(maxLon),
    latToWebMercatorY(minLat),
    latToWebMercatorY(maxLat),
  );
}

// normalizeTitilerSourceHost imported from @/utils/titilerSourceHost

function createPotreeRequestManager(baseUrl: string): {
  fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
  getUrl(url: string): Promise<string>;
} {
  const resolve = (url: RequestInfo | URL): string => {
    const raw = typeof url === 'string' ? url : url instanceof URL ? url.toString() : url.url;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/')) return raw;
    try {
      return new URL(raw, baseUrl || window.location.href).toString();
    } catch {
      return `${baseUrl}${raw}`;
    }
  };

  return {
    async getUrl(url: string): Promise<string> {
      return resolve(url);
    },
    async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const url = resolve(input);
      const headers = new Headers(init?.headers);
      const response = await fetch(url, {
        ...init,
        headers,
        // Chrome can fail cache operations on multi-GB range-fetched
        // Potree v2 octree.bin resources (ERR_CACHE_OPERATION_NOT_SUPPORTED).
        // Potree octree chunks are immutable for a mounted output, so bypass
        // the browser HTTP cache and let the loader manage runtime residency.
        cache: 'no-store',
      });
      if (url.endsWith('/metadata.json')) {
        return rewritePotreeMetadataResponse(response);
      }
      return response;
    },
  };
}

async function rewritePotreeMetadataResponse(response: Response): Promise<Response> {
  if (!response.ok) return response;
  const metadata = await response.clone().json().catch(() => null);
  if (!metadata || typeof metadata !== 'object') return response;
  const raw = metadata as Record<string, unknown>;

  // Older PotreeConverter v2 builds write `attributes`; the legacy v1
  // loader path in potree-core expects `pointAttributes`. The v2 path
  // (used here, because URL ends with /metadata.json) reads `attributes`
  // directly, so populating `pointAttributes` is only useful for the v1
  // fallback. Kept so the payload stays compatible across loader paths.
  if (!raw.pointAttributes && Array.isArray(raw.attributes)) {
    raw.pointAttributes = (raw.attributes as Array<Record<string, unknown>>).map((attr) => {
      const name = String(attr.name ?? '');
      if (name === 'position') return 'POSITION_CARTESIAN';
      if (name === 'rgba' || name === 'rgb') return 'RGBA_PACKED';
      if (name === 'intensity') return 'INTENSITY';
      if (name === 'classification') return 'CLASSIFICATION';
      if (name === 'return number') return 'RETURN_NUMBER';
      if (name === 'number of returns') return 'NUMBER_OF_RETURNS';
      if (name === 'point source id') return 'POINT_SOURCE_ID';
      if (name === 'user data') return 'USER_DATA';
      if (name === 'scan angle rank') return 'SCAN_ANGLE_RANK';
      return name.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
    });
  }

  if (!raw.hierarchyStepSize && typeof raw.hierarchy === 'object' && raw.hierarchy !== null) {
    const h = raw.hierarchy as Record<string, unknown>;
    raw.hierarchyStepSize = typeof h.stepSize === 'number' ? h.stepSize : 4;
  }

  // potree-core derives r*.bin URLs from octreeDir. For PotreeConverter v2
  // single-file output, keep octreeDir empty and let potree-core resolve
  // octree.bin / hierarchy.bin next to metadata.json when needed.
  if (!raw.octreeDir) raw.octreeDir = '.';

  const body = JSON.stringify(raw);
  const headers = new Headers(response.headers);
  headers.set('content-type', 'application/json');
  headers.set('content-length', String(new TextEncoder().encode(body).length));
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

function isCopcUrl(value: string | undefined): value is string {
  return typeof value === 'string' && /\.copc(?:\.laz)?(?:[?#].*)?$/i.test(value);
}

function isPotreeMetadataUrl(value: string | undefined): value is string {
  return typeof value === 'string' && /(?:^|\/)metadata\.json(?:[?#].*)?$/i.test(value);
}
